import { getApiBaseUrl } from '../../utils/apiConfig';
import { fetchPage, searchDocumentation, unwrapData } from './documentationService';

function extractVersionTokens(text) {
  const tokens = new Set();
  const str = String(text || '');
  const patterns = [/vers[aã]o\s+(\d+(?:\.\d+)+)/gi, /\b(\d+\.\d+(?:\.\d+)?)\b/g];
  for (const pattern of patterns) {
    let match = pattern.exec(str);
    while (match) {
      tokens.add(match[1].toLowerCase());
      match = pattern.exec(str);
    }
  }
  return [...tokens];
}

function extractPrimaryAnswerVersion(answerText) {
  const explicit = [...String(answerText || '').matchAll(/vers[aã]o\s+(\d+(?:\.\d+)+)/gi)];
  if (explicit.length) return explicit[explicit.length - 1][1];
  return extractVersionTokens(answerText)[0] || null;
}

function versionAlignmentScore(text, versionTokens) {
  if (!versionTokens?.length) return 0;
  const norm = normalizeMatchKey(text);
  let score = 0;
  for (const version of versionTokens) {
    const dotted = version.toLowerCase();
    const spaced = dotted.replace(/\./g, ' ');
    if (norm.includes(dotted) || norm.includes(spaced)) {
      score += 80;
      continue;
    }
    const digits = dotted.replace(/\./g, '');
    if (digits && norm.replace(/[^0-9]/g, '').includes(digits)) {
      score += 55;
    }
  }
  return score;
}

async function findVersionPageInBookstack(versionToken) {
  if (!versionToken) return null;
  const queries = [`Versão ${versionToken}`, versionToken];
  for (const query of queries) {
    try {
      const result = await searchDocumentation(query);
      const items = Array.isArray(unwrapData(result)) ? unwrapData(result) : [];
      let best = null;
      let bestScore = -1;
      for (const item of items) {
        if (item?.type !== 'page') continue;
        const score = versionAlignmentScore(stripHtml(item.name), [versionToken]);
        if (score > bestScore) {
          bestScore = score;
          best = item;
        }
      }
      if (best && bestScore >= 55) return best;
    } catch (_) {
      /* tenta próxima query */
    }
  }
  return null;
}

function sourceToQuery(source) {
  return String(source || '')
    .replace(/\.(md|pdf|html)$/i, '')
    .replace(/[-_]+/g, ' ')
    .trim();
}

function stripHtml(value) {
  return String(value || '').replace(/<[^>]+>/g, '').trim();
}

function normalizeMatchKey(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\.(md|pdf|html)$/i, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function sourceBasename(source) {
  const parts = String(source || '').split(/[/\\]/);
  return normalizeMatchKey(parts[parts.length - 1] || source);
}

function getResultModifiedTime(result) {
  const raw = result?.created_at || result?.createdAt || result?.updated_at || result?.updatedAt;
  if (!raw) return 0;
  const time = Date.parse(raw);
  return Number.isNaN(time) ? 0 : time;
}

function getSourceLabel(src) {
  const label = src?.title || src?.name || src?.page_title;
  if (label) return stripHtml(label);
  return sourceToQuery(src?.source);
}

function dedupeSources(sources) {
  const unique = [];
  const seen = new Set();
  for (const item of sources || []) {
    const key = String(item?.source || '').trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    unique.push(item);
  }
  return unique;
}

function rankSearchMatch(item, source, versionTokens = []) {
  const srcNorm = normalizeMatchKey(source);
  const baseNorm = sourceBasename(source);
  const name = normalizeMatchKey(stripHtml(item?.name));
  const book = normalizeMatchKey(item?.book?.name);
  const url = normalizeMatchKey(item?.url || '');

  let score = versionAlignmentScore(name, versionTokens);
  if (!srcNorm) return score;
  if (name && (name === srcNorm || name === baseNorm)) score = Math.max(score, 100);
  else if (baseNorm && name && (name.includes(baseNorm) || baseNorm.includes(name))) score = Math.max(score, 85);
  else if (baseNorm && url && url.includes(baseNorm.replace(/\s+/g, '-'))) score = Math.max(score, 75);
  else if (baseNorm && book && book.includes(baseNorm)) score = Math.max(score, 60);
  else {
    const srcWords = baseNorm.split(' ').filter((w) => w.length > 2);
    if (srcWords.length && name) {
      const nameWords = name.split(' ');
      const overlap = srcWords.filter((word) =>
        nameWords.some((part) => part.includes(word) || word.includes(part))
      ).length;
      score = Math.max(score, overlap * 12);
    }
  }

  return score;
}

function pickBestSearchResult(items, source, versionTokens = []) {
  const pool = (items || []).filter((item) => item?.type === 'page');
  const candidates = pool.length ? pool : (items || []);
  let best = null;
  let bestScore = -1;

  for (const item of candidates) {
    const score = rankSearchMatch(item, source, versionTokens);
    if (score > bestScore) {
      bestScore = score;
      best = item;
    }
  }

  if (!best || bestScore < 20) return null;
  return { item: best, matchScore: bestScore };
}

async function resolveSourcePage(src, versionTokens = []) {
  const pageId = src?.page_id ?? src?.pageId ?? (src?.type === 'page' ? src?.id : null);
  if (pageId) {
    try {
      const page = unwrapData(await fetchPage(pageId));
      if (page?.id) {
        return {
          ...src,
          searchResult: { ...page, type: 'page' },
          label: stripHtml(page.name) || getSourceLabel(src),
          modifiedTime: getResultModifiedTime(page),
          matchScore: 100,
          versionScore: versionAlignmentScore(stripHtml(page.name), versionTokens),
        };
      }
    } catch (_) {
      /* tenta busca abaixo */
    }
  }

  const query = sourceToQuery(src?.source);
  if (query.length < 2) return null;

  const result = await searchDocumentation(query);
  const items = Array.isArray(unwrapData(result)) ? unwrapData(result) : [];
  const match = pickBestSearchResult(items, src.source, versionTokens);
  if (!match) return null;

  return {
    ...src,
    searchResult: match.item,
    label: stripHtml(match.item.name) || getSourceLabel(src),
    modifiedTime: getResultModifiedTime(match.item),
    matchScore: match.matchScore,
    versionScore: versionAlignmentScore(stripHtml(match.item.name), versionTokens),
  };
}

function formatSourceMentionLabel(label) {
  const stripped = stripHtml(label);
  const versionMatch = stripped.match(/vers[aã]o\s+(\d+(?:\.\d+)+)/i);
  if (versionMatch) return versionMatch[1];
  return stripped.length > 26 ? `${stripped.slice(0, 24)}…` : stripped;
}

function labelDedupeKey(label) {
  return normalizeMatchKey(formatSourceMentionLabel(label));
}

function sortResolvedSources(a, b) {
  const versionDiff = (b.versionScore ?? 0) - (a.versionScore ?? 0);
  if (versionDiff !== 0) return versionDiff;
  const scoreDiff = (b.score ?? 0) - (a.score ?? 0);
  if (scoreDiff !== 0) return scoreDiff;
  if (b.matchScore !== a.matchScore) return b.matchScore - a.matchScore;
  return b.modifiedTime - a.modifiedTime;
}

async function resolveAllOryaSources(sources, answerText = '') {
  const unique = dedupeSources(sources);
  if (!unique.length) return [];

  const versionTokens = extractVersionTokens(answerText);
  const primaryVersion = extractPrimaryAnswerVersion(answerText);
  const ranked = [...unique].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));

  const resolved = (await Promise.all(
    ranked.map((src) => resolveSourcePage(src, versionTokens).catch(() => null))
  )).filter(Boolean);

  if (primaryVersion) {
    const versionPage = await findVersionPageInBookstack(primaryVersion);
    if (versionPage) {
      const entry = {
        source: versionPage.name,
        searchResult: versionPage,
        label: stripHtml(versionPage.name) || `Versão ${primaryVersion}`,
        modifiedTime: getResultModifiedTime(versionPage),
        matchScore: 100,
        versionScore: 100,
      };
      const exists = resolved.some((item) => item.searchResult?.id === versionPage.id);
      if (!exists) resolved.unshift(entry);
    }
  }

  return resolved.sort(sortResolvedSources);
}

/**
 * @returns {{ primary: object|null, others: string[] }}
 */
export async function resolveOryaSources(sources, answerText = '') {
  const resolved = await resolveAllOryaSources(sources, answerText);

  if (!resolved.length) {
    const unique = dedupeSources(sources);
    if (!unique.length) return { primary: null, others: [] };
    const top = [...unique].sort((a, b) => (b.score ?? 0) - (a.score ?? 0))[0];
    return {
      primary: { ...top, searchResult: null, label: getSourceLabel(top) },
      others: [],
    };
  }

  const primary = resolved[0];
  const seen = new Set([labelDedupeKey(primary.label)]);
  const others = [];

  for (const item of resolved.slice(1)) {
    const mention = formatSourceMentionLabel(item.label);
    const key = labelDedupeKey(item.label);
    if (!mention || seen.has(key)) continue;
    seen.add(key);
    others.push(mention);
  }

  return { primary, others };
}

/**
 * Escolhe a fonte principal entre as citadas pelo RAG,
 * alinhada à versão citada na resposta quando houver.
 */
export async function resolveMostRecentOryaSource(sources, answerText = '') {
  const { primary } = await resolveOryaSources(sources, answerText);
  return primary;
}

export async function resolveOryaSourceForNavigation(sourceItem, answerText = '') {
  if (!sourceItem) return null;
  if (sourceItem.searchResult?.id) return sourceItem;

  const versionTokens = extractVersionTokens(answerText);
  try {
    const resolved = await resolveSourcePage(sourceItem, versionTokens);
    return resolved || sourceItem;
  } catch (_) {
    return sourceItem;
  }
}

async function oryaRequest(path, body) {
  const token = localStorage.getItem('authToken');
  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok || data?.success === false) {
    throw new Error(data?.message || `Erro ao consultar a Orya (${response.status})`);
  }
  return data;
}

/**
 * @param {{ question: string, history?: Array<{ role: string, content: string }>, pageScope?: object|null }} payload
 */
export async function sendDocumentationOryaQuery(payload) {
  const body = {
    question: payload.question,
    history: payload.history,
  };
  if (payload.pageScope) {
    body.page_scope = payload.pageScope;
  }
  const result = await oryaRequest('/api/documentation/orya/query', body);
  return result?.data || {};
}

export async function submitAprenderOryaFeedback(payload) {
  const result = await oryaRequest('/api/documentation/orya/feedback', payload);
  return result?.data || {};
}
